<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import { getSchoolsHierarchy, syncManager } from "../lib/sync.js";
    import { db } from "../db/index.js";
    import ResourceList from "@edu-quizz/shared/components/ResourceList.svelte";
    import PWAInstallBanner from "../lib/components/PWAInstallBanner.svelte";
    import NotificationBanner from "../lib/components/NotificationBanner.svelte";
    import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";
    import { syncStatus, isOnline } from "../stores.js";

    let schools = [];
    let myResources = [];
    let loading = true;
    let error = null;
    let activeView = "classroom"; // "classroom" or "personal"

    async function loadResourceDetails(resourceId, resourceType) {
        try {
            let details = null;
            switch (resourceType) {
                case RESOURCE_TYPES.FLASHCARD_DECK:
                    details = await db.getFlashcardDeck(resourceId);
                    break;
                case RESOURCE_TYPES.QUIZ:
                    details = await db.getQuiz(resourceId);
                    break;
                case RESOURCE_TYPES.ARTICLE:
                    details = await db.getArticleWithAttachedResources(resourceId);
                    break;
                case RESOURCE_TYPES.BOOKMARK:
                    details = await db.getBookmark(resourceId);
                    break;
                case RESOURCE_TYPES.TABLE_OF_CONTENTS:
                    details = await db.getToc(resourceId);
                    break;
            }

            return details;
        } catch (err) {
            return null;
        }
    }

    async function loadSchools() {
        loading = true;
        error = null;

        try {
            // Load user's own resources
            myResources = await db.myResources.toArray();

            // Get schools from local database
            const result = await getSchoolsHierarchy();

            if (result.success) {
                schools = result.data;
            } else {
                error = result.error;
            }
        } catch (err) {
            error = err.message;
        } finally {
            loading = false;
        }
    }

    function handleSyncComplete() {
        loadSchools();
    }

    function navigateToEditor() {
        window.location.href = "/student";
    }

    function handleResourceClick(resource, details) {
        console.log("[ handleResourceClick ] click", resource, details);
        const resourceType = resource.resourceType;
        const resourceId = resource.resourceId;

        // Handle different resource types
        switch (resourceType) {
            case RESOURCE_TYPES.QUIZ:
                push(`/quiz/${resourceId}`);
                break;
            case RESOURCE_TYPES.FLASHCARD_DECK:
                push(`/flashcard/${resourceId}`);
                break;
            case RESOURCE_TYPES.ARTICLE:
                push(`/article/${resourceId}`);
                break;
            case RESOURCE_TYPES.BOOKMARK:
                // Open external URL in new tab
                if (details?.url) {
                    window.open(details.url, "_blank", "noopener,noreferrer");
                }
                break;
            case RESOURCE_TYPES.TABLE_OF_CONTENTS:
                push(`/toc/${resourceId}`);
                break;
        }
    }

    async function prepareResourceItems(resources) {
        const items = [];

        for (const resource of resources) {
            const details = await loadResourceDetails(resource.resourceId, resource.resourceType);

            const item = {
                resource,
                details,
                nestedResources: [],
            };

            // For articles, extract and transform nested resources
            if (resource.resourceType === RESOURCE_TYPES.ARTICLE && details?.attachedResources) {
                const nestedItems = [];
                for (const attachedResource of details.attachedResources) {
                    console.log("nested ", attachedResource);
                    const nestedDetails = await loadResourceDetails(
                        attachedResource.serverResourceId,
                        attachedResource.type
                    );

                    nestedItems.push({
                        resource: {
                            ...attachedResource,
                            resourceId: attachedResource.serverResourceId,
                            resourceType: attachedResource.type
                        },
                        details: nestedDetails,
                        nestedResources: [],
                    });
                }
                item.nestedResources = nestedItems;
            }

            items.push(item);
        }

        return items;
    }

    onMount(async () => {
        // Mettre à jour le compteur d'éléments en attente
        await syncStatus.updatePendingCount();

        await loadSchools();

        // Déclencher automatiquement la synchronisation s'il y a des éléments en attente
        const hasPendingSync = $syncStatus.pendingCount > 0;
        const online = $isOnline;

        if (hasPendingSync && online && !$syncStatus.isSyncing) {
            console.log(`Déclenchement automatique de la synchronisation (${$syncStatus.pendingCount} éléments en attente)`);
            syncManager.fullSync();
        }

        window.addEventListener("syncComplete", handleSyncComplete);
        return () => {
            window.removeEventListener("syncComplete", handleSyncComplete);
        };
    });
</script>

<div class="home">
    <PWAInstallBanner />
    <NotificationBanner />

    <!-- Dock Navigation -->
    <div class="dock">
        <button
            class:dock-active={activeView === "classroom"}
            on:click={() => (activeView = "classroom")}
        >
            <svg
                class="size-[1.2em]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
            >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span class="dock-label">Ressources de classes</span>
        </button>

        <button
            class:dock-active={activeView === "personal"}
            on:click={() => (activeView = "personal")}
        >
            <svg
                class="size-[1.2em]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
            >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
            </svg>
            <span class="dock-label">Mes ressources</span>
        </button>
    </div>

    {#if loading}
        <div class="loading">Chargement des ressources...</div>
    {:else if error}
        <div class="error">
            <p>Erreur: {error}</p>
            <button on:click={loadSchools}>Réessayer</button>
        </div>
    {:else if schools.length === 0 && myResources.length === 0}
        <div class="empty">
            <p>Aucune ressource disponible.</p>
            <p>Cliquez sur "Synchroniser" pour télécharger vos ressources.</p>
        </div>
    {:else}
        <!-- Classroom Resources View -->
        {#if activeView === "classroom"}
            <div class="schools-list">
                {#if schools.length === 0}
                    <div class="empty">
                        <p>Aucune ressource de classe disponible.</p>
                        <p>
                            Cliquez sur "Synchroniser" pour télécharger vos
                            ressources.
                        </p>
                    </div>
                {:else}
                    {#each schools as school}
                        <div class="school-section">
                            {#if school.classrooms && school.classrooms.length > 0}
                                {#each school.classrooms as classroom}
                                    <div class="classroom-section">
                                        <div class="classroom-header">
                                            <h2><span class="school-name">{school.name}</span> <span class="separator">›</span> {classroom.name}</h2>
                                        </div>

                                        {#if classroom.resources && classroom.resources.length > 0}
                                            {#await prepareResourceItems(classroom.resources)}
                                                <p class="no-resources">Chargement...</p>
                                            {:then items}
                                                <ResourceList
                                                    {items}
                                                    onItemClick={handleResourceClick}
                                                />
                                            {:catch error}
                                                <p class="no-resources">Erreur: {error.message}</p>
                                            {/await}
                                        {:else}
                                            <p class="no-resources">
                                                Aucune ressource assignée
                                            </p>
                                        {/if}
                                    </div>
                                {/each}
                            {:else}
                                <p class="no-classrooms">Aucune classe pour {school.name}</p>
                            {/if}
                        </div>
                    {/each}
                {/if}
            </div>
        {/if}

        <!-- Personal Resources View -->
        {#if activeView === "personal"}
            <div class="header">
                <h1>Mes ressources</h1>
            </div>
            <div
                tabindex="0"
                class="collapse collapse-arrow bg-base-100 border-base-300 border mb-6"
            >
                <div class="collapse-title font-semibold">
                    Créer mes propres flashcards
                </div>
                <div class="collapse-content text-sm">
                    <p class="mb-4">
                        L'éditeur vous permet de créer vos propres jeux de
                        flashcards personnalisés. Ces flashcards ne sont
                        accessibles qu'à vous et vous pouvez les modifier à tout
                        moment.
                    </p>
                    <button
                        class="btn btn-primary btn-sm"
                        on:click={navigateToEditor}
                    >
                        Accéder à l'éditeur
                    </button>
                </div>
            </div>

            {#if myResources.length > 0}
                <div class="my-resources-section">
                    {#await prepareResourceItems(myResources)}
                        <p>Chargement...</p>
                    {:then items}
                        <ResourceList
                            {items}
                            onItemClick={handleResourceClick}
                        />
                    {:catch error}
                        <p class="error">Erreur: {error.message}</p>
                    {/await}
                </div>
            {:else}
                <div class="empty">
                    <p>Aucune ressource personnelle.</p>
                    <p>
                        Utilisez l'éditeur pour créer vos propres flashcards.
                    </p>
                </div>
            {/if}
        {/if}
    {/if}
</div>

<style>
    .home {
        padding: 1.5rem;
        padding-bottom: 5rem;
        max-width: 1200px;
        margin: 0 auto;
        text-align: left;
    }

    .header {
        margin-bottom: 1.5rem;
    }

    h1 {
        font-size: 1.5rem;
        font-weight: 700;
        margin-bottom: 0.75rem;
        color: var(--color-base-content, #1f2937);
    }

    h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: var(--color-base-content, #374151);
    }

    .school-name {
        font-weight: 400;
        color: var(--color-base-content, #6b7280);
    }

    .separator {
        margin: 0 0.25rem;
        color: var(--color-base-content, #9ca3af);
    }




    .loading,
    .empty {
        text-align: center;
        padding: 3rem;
        color: #666;
    }

    .error {
        background: #fee;
        border: 1px solid #fcc;
        padding: 1rem;
        border-radius: 6px;
        color: #c33;
    }

    .error button {
        margin-top: 0.5rem;
        background: #c33;
        color: white;
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        cursor: pointer;
    }

    .my-resources-section {
        margin-bottom: 1.5rem;
    }

    .schools-list {
        display: flex;
        flex-direction: column;
        gap: 3rem;
    }

    .description {
        color: #666;
        font-size: 0.875rem;
        margin: 0;
        line-height: 1.5;
    }


    .no-resources,
    .no-classrooms {
        color: #999;
        font-style: italic;
        font-size: 0.875rem;
        margin: 0.5rem 0;
    }
</style>
